import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { DelayService, WindowRef } from '@trademe/tangram';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, first, takeUntil, tap } from 'rxjs/operators';
import { PHOTO_LOAD_TIMEOUT } from '../constants/photo.constants';
import { BoundsEnum } from '../enums/bounds';
import { CornerPositionEnum } from '../enums/corner-position';
import { LockPointEnum } from '../enums/lockpoint';
import { Box } from '../models/box';
import { EditPhoto } from '../models/edit-photo.viewmodel';
import { Point } from '../models/point';
import { BoundingBoxService } from '../services/bounding-box/bounding-box.service';
import { PhotoLoaderService } from '../services/loader/photo-loader.service';
import { PhotoTransformService } from '../services/transform/photo-transform.service';

const CROP_CORNER_OFFSET = -30;
const CROP_CORNER_WIDTH = 60;

interface ICornerProperties {
    position: CornerPositionEnum;
    lockPoint: number;
}

/**
 * This editor is operating on two different coordinate systems:
 * - one coordinate system is representing the actual UI that the user is interacting with (the canvas)
 * - one coordinate system is representing the native dimensions of the uploaded picture
 *
 * When the image is placed in the canvas, we need to scale it up or down to match the available space.
 * Thereby we need to translate its dimensions from the image coordinate system into the UI coordinate system.
 * Once we are done with rotating and/or cropping (in the UI coordinate system) we need to apply the rotation
 * and/or cropbox to the image coordinate system.
 *
 * The dimensions in the photo upload model are always referring to the image coordinate system, because that is
 * what the server expects.
 */
@Component({
    selector: 'tm-list-progress-photo-editor',
    templateUrl: './photo-editor.component.html',
    styleUrls: ['./photo-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PhotoEditorComponent
    implements AfterViewInit, OnDestroy, OnInit, OnChanges {
    @ViewChild('canvasImage', { static: true }) public canvasImage: ElementRef;
    @ViewChild('editPhotoCanvas', { static: true }) public canvas: ElementRef;
    @ViewChild('cropMask', { static: true }) public cropMask: ElementRef;
    @ViewChild('canvasCropArea', { static: true })
    public canvasCropArea: ElementRef;

    @ViewChild('cornerTopLeft') public cornerTopLeft: ElementRef;
    @ViewChild('cornerTopRight') public cornerTopRight: ElementRef;
    @ViewChild('cornerBottomLeft') public cornerBottomLeft: ElementRef;
    @ViewChild('cornerBottomRight') public cornerBottomRight: ElementRef;

    @Input() public photo: EditPhoto;

    public mask = new Box(new Point(), new Point());
    public cropCornerOffsetX: number = CROP_CORNER_OFFSET;
    public cropCornerOffsetY: number = CROP_CORNER_OFFSET;
    public cropCornerWidth: number = CROP_CORNER_WIDTH;
    public cropCornerHeight: number = CROP_CORNER_WIDTH;
    public isCropMaskInvalid: boolean;
    public lastDraggedEventTarget: EventTarget;

    private _context: CanvasRenderingContext2D;
    private _image: HTMLImageElement;

    private _mousemoveListener: () => void;
    private _mouseupListener: () => void;
    private _mark: Point = new Point();
    private _cornerPositionMap = new Map<unknown, ICornerProperties>();

    private _destroyed$: Subject<any> = new Subject<any>();

    constructor(
        private _elementRef: ElementRef,
        private _changeDetectorRef: ChangeDetectorRef,
        private _boundingBoxService: BoundingBoxService,
        private _photoTransformService: PhotoTransformService,
        private _photoLoaderService: PhotoLoaderService,
        private _renderer: Renderer2,
        private _delayService: DelayService,
        private _zone: NgZone,
        private _windowRef: WindowRef
    ) {}

    public onWindowResize = () => {
        if (this.canvasImage.nativeElement.offsetWidth != null) {
            this.updatePhoto();
        }
    };

    public ngOnDestroy(): void {
        this._destroyed$.next(true);
    }

    public ngOnInit(): void {
        const HIDDEN_CLASS = 'tm-list-progress-photo-editor--hidden';
        this._zone.runOutsideAngular(() => {
            const canvasCropAreaClassList = this.canvasCropArea.nativeElement
                .classList;

            this._windowRef.useWindow((window) => {
                fromEvent(window, 'resize')
                    .pipe(
                        takeUntil(this._destroyed$),
                        tap(() => {
                            this._zone.run(() => {
                                if (
                                    !canvasCropAreaClassList.contains(
                                        HIDDEN_CLASS
                                    )
                                ) {
                                    canvasCropAreaClassList.add(HIDDEN_CLASS);
                                }
                            });
                        }),
                        debounceTime(200),
                        tap(() => {
                            this._zone.run(() => {
                                if (this.hasCropBox()) {
                                    canvasCropAreaClassList.remove(
                                        HIDDEN_CLASS
                                    );
                                }
                            });
                        })
                    )
                    .subscribe(this.onWindowResize);
            });
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { photo } = changes;
        if (photo && photo.currentValue) {
            this.updatePhoto();
        }
    }

    public ngAfterViewInit(): void {
        this._cornerPositionMap
            .set(this.cornerTopLeft.nativeElement, {
                position: CornerPositionEnum.topLeft,
                lockPoint: LockPointEnum.bottomRight
            })
            .set(this.cornerTopRight.nativeElement, {
                position: CornerPositionEnum.topRight,
                lockPoint: LockPointEnum.bottomLeft
            })
            .set(this.cornerBottomLeft.nativeElement, {
                position: CornerPositionEnum.bottomLeft,
                lockPoint: LockPointEnum.topRight
            })
            .set(this.cornerBottomRight.nativeElement, {
                position: CornerPositionEnum.bottomRight,
                lockPoint: LockPointEnum.topLeft
            })
            .set(this.cropMask.nativeElement, {
                position: CornerPositionEnum.box,
                lockPoint: -1
            });
        this.initCanvas();
    }

    public onDragStart(event: MouseEvent | TouchEvent): void {
        if (event.type === 'touchstart') {
            const touchEvent = <TouchEvent>event;
            this._mark = new Point(
                touchEvent.changedTouches[0].clientX,
                touchEvent.changedTouches[0].clientY
            );
        } else {
            const mouseEvent = <MouseEvent>event;

            this.lastDraggedEventTarget =
                mouseEvent.target === mouseEvent.currentTarget
                    ? mouseEvent.target
                    : mouseEvent.currentTarget;

            this._mark = new Point(mouseEvent.clientX, mouseEvent.clientY);

            this._mousemoveListener = this._renderer.listen(
                'document',
                'mousemove',
                (e) => {
                    if (!this.lastDraggedEventTarget === e.target.id) {
                        return;
                    }
                    this.onMouseDrag(this.lastDraggedEventTarget, e);
                    e.preventDefault();
                }
            );

            this._mouseupListener = this._renderer.listen(
                'document',
                'mouseup',
                (e) => {
                    this.onDragEnd(e);
                    e.preventDefault();
                }
            );

            event.preventDefault();
        }
    }

    public onDragEnd(event: MouseEvent | TouchEvent): void {
        if (this._mousemoveListener) {
            this._mousemoveListener();
        }

        if (this._mouseupListener) {
            this._mouseupListener();
        }
        event.preventDefault();
    }

    public onMouseDrag(target: EventTarget, event: MouseEvent): void {
        this.updateElementPosition(
            this._cornerPositionMap.get(target),
            new Point(event.clientX, event.clientY),
            event.shiftKey
        );
    }

    public onTouchDrag(event: TouchEvent): void {
        this.updateElementPosition(
            this._cornerPositionMap.get(event.currentTarget),
            new Point(
                event.changedTouches[0].clientX,
                event.changedTouches[0].clientY
            )
        );
    }

    hasCropBox(): boolean {
        return this._photoTransformService.hasCropBox(this.photo);
    }

    private updatePhoto(): void {
        this._photoLoaderService
            .loadImageFromUrl(this.photo.plusSize, PHOTO_LOAD_TIMEOUT)
            .pipe(first(), takeUntil(this._destroyed$))
            .subscribe((image) => {
                this._image = image;

                // Pretty hacky - needs a resolved layout to get offsetWidth/Height
                this._delayService.createDelay(0).subscribe(() => {
                    this.updatePhotoImage(this._image);
                });
            });
    }

    private initCanvas(): void {
        const canvas = this.canvas.nativeElement;
        if (!canvas) {
            return;
        }

        this._context = <CanvasRenderingContext2D>canvas.getContext('2d');
        this.updatePhoto();
    }

    private updateElementPosition(
        cornerProperties: ICornerProperties,
        newPosition: Point,
        maintainAspectRatio = false
    ): void {
        if (newPosition.x === 0 || newPosition.y === 0) {
            return;
        }

        const currentPosition = cornerProperties.position;
        let offset = newPosition.subtract(this._mark);

        if (maintainAspectRatio && currentPosition !== CornerPositionEnum.box) {
            let diffY = offset.y;
            let diffX = offset.x;

            if (Math.abs(diffY) > Math.abs(diffX)) {
                diffX = (diffX < 0 ? -1 : 1) * Math.abs(diffY);
            } else {
                diffY = (diffY < 0 ? -1 : 1) * Math.abs(diffX);
            }
            offset = new Point(diffX, diffY);
        }

        const target = this.mask.getCorner(currentPosition);

        if (target) {
            const height = this.mask.height;
            const width = this.mask.width;

            this.mask.setCorner(currentPosition, target.add(offset));
            if (currentPosition === CornerPositionEnum.box) {
                this.mask.bottomRight = new Point(
                    this.mask.topLeft.x + width,
                    this.mask.topLeft.y + height
                );
            }
        }

        this.applyCropBox(cornerProperties.lockPoint);
        this.updatePhoto();

        this._mark = newPosition;
    }

    private updatePhotoImage(image: HTMLImageElement): void {
        if (!image || !image.width) {
            return;
        }

        const canvas = this._context.canvas;
        const rotation = this._photoTransformService.getPhotoRotation(
            this.photo,
            0
        );

        const canvasImageElement = this.canvasImage.nativeElement;
        if (canvasImageElement && canvasImageElement.offsetWidth) {
            const maxDimension = Math.max(
                canvasImageElement.offsetWidth,
                canvasImageElement.offsetHeight
            );
            canvas.width = maxDimension;
            canvas.height = maxDimension;
        }

        this._context.clearRect(0, 0, canvas.width, canvas.height);
        this._context.save();
        this._context.translate(canvas.width / 2, canvas.height / 2);
        this._context.rotate((rotation * Math.PI) / 180);
        this._context.translate(-canvas.width / 2, -canvas.height / 2);
        const draw = this.getImageDrawDimensions(image);
        this._context.drawImage(
            image,
            0,
            0,
            image.width,
            image.height,
            draw.x,
            draw.y,
            draw.width,
            draw.height
        );
        this._context.restore();
        this._image = image;

        this.photo.imageWidth = image.width;
        this.photo.imageHeight = image.height;

        this.updateCrop();

        this._changeDetectorRef.detectChanges();
    }

    private updateCrop(): void {
        const nativeElem = this._elementRef.nativeElement;
        if (!nativeElem) {
            return;
        }

        if (!this._image) {
            return;
        }

        if (!this.hasCropBox()) {
            return;
        }

        const rotation = this._photoTransformService.getPhotoRotation(
            this.photo,
            0
        );

        this.mask = this.getImageCropDimensions(this._image, rotation);

        this.isCropMaskInvalid = !this._photoTransformService.isCropBoxValid(
            this.photo
        );
    }

    private applyCropBox(lockPoint: number): void {
        const rotation = this._photoTransformService.getPhotoRotation(
            this.photo,
            0
        );
        const [imageWidth, imageHeight, ratio] = this.getScaledImageDimensions(
            this._image,
            rotation
        );
        const canvas = this._context.canvas;
        this.mask = this.restrictBox(
            this.mask,
            imageWidth,
            imageHeight,
            canvas.width,
            canvas.height,
            lockPoint
        );
        const box = this.mask;
        const photoCropBox = this.photo.cropBox;
        // after we have restricted the cropbox in UI coordinates, we need to scale it to the actual image dimensions
        // and apply that to the photo upload model
        photoCropBox.x =
            (box.topLeft.x - (canvas.width - imageWidth) / 2) / ratio;
        photoCropBox.y =
            (box.topLeft.y - (canvas.height - imageHeight) / 2) / ratio;
        photoCropBox.width = box.width / ratio;
        photoCropBox.height = box.height / ratio;
        this._photoTransformService.applyCropBox(this.photo, lockPoint);
    }

    private restrictBox(
        box: Box,
        imageWidth: number,
        imageHeight: number,
        canvasWidth: number,
        canvasHeight: number,
        lockPoint: number
    ): Box {
        const offsetX = (canvasWidth - imageWidth) / 2;
        const offsetY = (canvasHeight - imageHeight) / 2;

        // so this is a bit fucked up:
        // the bounding box service assumes that it works on an image that starts in the origin (0,0)
        // if our image is not perfectly square it is either centered vertically or horizontally, so there
        // is going to be either a vertical or horizontal offset (calculated by taking half the difference between
        // width/height of the canvas and the image)
        // we therefore need to move the cropbox by that negative vertical or horizontal offset as well
        const cropBox = [
            box.topLeft.x - offsetX,
            box.topLeft.y - offsetY,
            box.width,
            box.height
        ];
        const outerBounds = [0, 0, imageWidth, imageHeight];
        const minWidth = this.cropCornerWidth;
        const minHeight = this.cropCornerHeight;
        const restrictedCropBox = this._boundingBoxService.restrictCropBoxToOuterBounds(
            cropBox,
            outerBounds,
            lockPoint,
            minWidth,
            minHeight
        );

        // after we restricted the cropbox, we need to add the vertical or horizontal offset to the cropbox again,
        // so that it is correctly placed in the canvas
        restrictedCropBox[BoundsEnum.x] += offsetX;
        restrictedCropBox[BoundsEnum.y] += offsetY;

        return this.getBoxFromBounds(restrictedCropBox);
    }

    private getBoxFromBounds(bounds: number[]): Box {
        const topLeft = new Point(bounds[BoundsEnum.x], bounds[BoundsEnum.y]);
        return new Box(
            topLeft,
            new Point(
                topLeft.x + bounds[BoundsEnum.width],
                topLeft.y + bounds[BoundsEnum.height]
            )
        );
    }

    private getScaledImageDimensions(
        image: HTMLImageElement,
        rotation: number
    ): number[] {
        let imageWidth = image.width;
        let imageHeight = image.height;
        if (rotation === 90 || rotation === 270) {
            imageWidth = image.height;
            imageHeight = image.width;
        }

        const canvas = this._context.canvas;

        const hRatio = canvas.width / imageWidth;
        const vRatio = canvas.height / imageHeight;
        const ratio = Math.min(hRatio, vRatio);

        imageWidth = imageWidth * ratio;
        imageHeight = imageHeight * ratio;

        return [imageWidth, imageHeight, ratio];
    }

    private getImageCropDimensions(
        image: HTMLImageElement,
        rotation: number
    ): Box {
        const cropBox = this.photo.cropBox;
        const [imageWidth, imageHeight, ratio] = this.getScaledImageDimensions(
            image,
            rotation
        );
        const canvas = this._context.canvas;

        const width = cropBox.width * ratio;
        const height = cropBox.height * ratio;
        const x = (canvas.width - imageWidth) / 2 + cropBox.x * ratio;
        const y = (canvas.height - imageHeight) / 2 + cropBox.y * ratio;

        return new Box(new Point(x, y), new Point(x + width, y + height));
    }

    private getImageDrawDimensions(image: HTMLImageElement): any {
        const canvas = this._context.canvas;
        const hRatio = canvas.width / image.width;
        const vRatio = canvas.height / image.height;
        const ratio = Math.min(hRatio, vRatio);
        const width = image.width * ratio;
        const height = image.height * ratio;
        const x = (canvas.width - width) / 2;
        const y = (canvas.height - height) / 2;

        return { x, y, width, height };
    }
}
